import React from 'react';

import { useTranslation } from 'next-i18next';

import type { Props as ButtonProps } from 'src/components/ui/Button';
import Button from 'src/components/ui/Button';
import Icon from 'src/components/ui/Icon';

import useFollowUnfollow from 'src/hooks/useFollowUnfollow';
import type { Company, User } from 'src/types/__generated__/graphql';
import { styledComponent } from 'src/utils/styled';
import useUser from 'src/hooks/useUser';

const StyledButton = styledComponent(Button)`
    span {
        text-transform: none;
    }
`;

const StyledIcon = styledComponent(Icon)`
    margin: -0.25rem;
`;

type Props = {
    user?: NonNullable<Pick<User, 'id' | 'firstName' | 'isFollowed'>>;
    company?: Pick<Company, 'id' | 'name' | 'isFollowingCompany'>;
    refetchQueries?: string[];
    followButtonProps?: ButtonProps & { 'data-target-id'?: string };
};

const FollowUnFollowButton: React.FC<Props> = ({ user, company, refetchQueries = [], followButtonProps }) => {
    const { t } = useTranslation(['profile']);
    const currentUser = useUser();
    const { isFollowing, handleFollowClick, handleUnFollowClick, loading } = useFollowUnfollow({
        user,
        company,
        refetchQueries,
    });
    if (isFollowing === undefined) {
        return null;
    }

    const sharedProps: Partial<ButtonProps> = {
        sx: {
            height: '2.5rem',
            pr: '0.75rem',
        },
        size: 'medium',
        variant: 'outlined',
        disabled: loading,
    };

    // Don't display button if the user is the current user
    if (user && currentUser && user.id === currentUser.id) {
        return null;
    }

    return isFollowing ? (
        <StyledButton
            {...sharedProps}
            onClick={handleUnFollowClick}
            startIcon={<StyledIcon name="Minus" color="inherit" />}
            {...followButtonProps}
        >
            {t('profile:unfollow')}
        </StyledButton>
    ) : (
        <StyledButton
            {...sharedProps}
            onClick={handleFollowClick}
            startIcon={<StyledIcon name="PlusThin" color="inherit" />}
            {...followButtonProps}
        >
            {t('profile:follow')}
        </StyledButton>
    );
};

export default FollowUnFollowButton;
